import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, Send, Volume2, VolumeX, X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { streamChat, classify, transcribe } from "@/lib/api";
import { createSpeaker } from "@/lib/speaker";
import { getSession, saveSession } from "@/lib/storage";
import { getDeviceId } from "@/lib/device";
import { useVisualViewportHeight } from "@/hooks/useVisualViewport";

const OPENING_TEXT = "Hey. What's on your mind today?";

export default function Chat() {
  const { sessionId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const mode = params.get("mode") === "figure" ? "figure" : "vent";

  const existing = useMemo(() => getSession(sessionId), [sessionId]);

  const [messages, setMessages] = useState(() => {
    // Stable IDs for React keys. Back-fill IDs for legacy stored sessions
    // that predate this change so their messages don't collide during
    // mutations (e.g. the retry-rollback path).
    const seed = existing?.messages || [
      { role: "assistant", text: OPENING_TEXT },
    ];
    return seed.map((m, i) =>
      m.id ? m : { ...m, id: `m_${existing?.createdAt || Date.now()}_${i}` }
    );
  });
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState("");
  const [input, setInput] = useState("");
  const [muted, setMuted] = useState(false);
  const [recording, setRecording] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const vh = useVisualViewportHeight();

  const speaker = useRef(null);
  const abortRef = useRef(null);
  const scrollRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const openingSpokenRef = useRef(false);

  // Persist session shell on first render.
  useEffect(() => {
    if (!existing) {
      saveSession({
        id: sessionId,
        mode,
        createdAt: Date.now(),
        messages: [{ role: "assistant", text: OPENING_TEXT }],
      });
    }
  }, []);

  // Init speaker.
  useEffect(() => {
    speaker.current = createSpeaker({ voice: "shimmer" });
    const off = speaker.current.onChange(({ speaking }) => setSpeaking(speaking));
    return () => {
      off();
      speaker.current?.stop();
    };
  }, []);

  // Autoscroll on new content.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages, streamText]);

  // iOS Safari standalone: lock the document body while Chat is mounted so
  // (a) iOS' focus-scroll can't yank the layout viewport upward past the
  // status bar when the input is tapped, and (b) our vh-driven container
  // height alone controls the composer's vertical position. On unmount we
  // restore everything to how it was.
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const saved = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyBottom: body.style.bottom,
      bodyWidth: body.style.width,
    };
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = "0";
    body.style.left = "0";
    body.style.right = "0";
    body.style.bottom = "0";
    body.style.width = "100%";
    const undoShift = () => {
      // Undo any residual scroll iOS applied trying to bring the input in view.
      if (window.scrollY !== 0 || window.scrollX !== 0) window.scrollTo(0, 0);
    };
    window.visualViewport?.addEventListener("resize", undoShift);
    window.visualViewport?.addEventListener("scroll", undoShift);
    undoShift();
    return () => {
      html.style.overflow = saved.htmlOverflow;
      body.style.overflow = saved.bodyOverflow;
      body.style.position = saved.bodyPosition;
      body.style.top = saved.bodyTop;
      body.style.left = saved.bodyLeft;
      body.style.right = saved.bodyRight;
      body.style.bottom = saved.bodyBottom;
      body.style.width = saved.bodyWidth;
      window.visualViewport?.removeEventListener("resize", undoShift);
      window.visualViewport?.removeEventListener("scroll", undoShift);
    };
  }, []);

  const persist = (nextMessages) => {
    saveSession({
      id: sessionId,
      mode,
      createdAt: existing?.createdAt || Date.now(),
      messages: nextMessages,
    });
  };

  const send = async (text) => {
    const trimmed = (text || "").trim();
    if (!trimmed || streaming) return;
    // Any explicit send is a fresh user gesture — unlock iOS audio.
    speaker.current?.unlock();
    setInput("");
    const nextMessages = [
      ...messages,
      { id: `m_${Date.now()}_u`, role: "user", text: trimmed },
    ];
    setMessages(nextMessages);
    persist(nextMessages);
    setStreaming(true);
    setStreamText("");

    // Build history from PRIOR turns only (backend will add current turn).
    const history = messages.map((m) => ({ role: m.role, text: m.text }));

    abortRef.current = streamChat({
      sessionId,
      mode,
      message: trimmed,
      history,
      deviceId: getDeviceId(),
      onDelta: (_delta, full) => setStreamText(full),
      onSentence: (s) => {
        if (!muted) speaker.current?.speak(s);
      },
      onDone: async (full) => {
        setStreaming(false);
        setStreamText("");
        const finalized = [
          ...nextMessages,
          { id: `m_${Date.now()}_a`, role: "assistant", text: full },
        ];
        setMessages(finalized);
        persist(finalized);
        // Auto-converge check
        const userTurns = finalized.filter((m) => m.role === "user").length;
        if (userTurns >= 5) {
          try {
            const { decision } = await classify(
              finalized.map((m) => ({ role: m.role, text: m.text }))
            );
            if (decision === "CLOSE") {
              // Give the user a moment to read the last reply.
              setTimeout(() => navigate(`/breathe/${sessionId}`), 3500);
            }
          } catch {
            /* ignore */
          }
        }
      },
      onError: (err) => {
        setStreaming(false);
        setStreamText("");
        const kind = err && err.message;
        if (kind === "stream_idle_timeout" || kind === "stream_closed_without_reply") {
          toast(
            "The connection went quiet. Tap send again — your message is still here.",
            { duration: 6000 }
          );
          // Restore the user's input so they can just re-tap send.
          setInput(trimmed);
          // Roll back the optimistic user bubble so it isn't duplicated.
          setMessages(messages);
          persist(messages);
        } else {
          toast.error("Something went quiet. Try again in a moment.");
        }
      },
    });
  };

  // Speak the opening line the first time (after any user gesture).
  const handleFirstGestureOpening = () => {
    // iOS Safari standalone: any tap unlocks audio for the whole session.
    speaker.current?.unlock();
    if (openingSpokenRef.current || muted) return;
    openingSpokenRef.current = true;
    speaker.current?.speak(OPENING_TEXT);
  };

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    speaker.current?.setMuted(next);
  };

  const startRecording = async () => {
    if (recording) return;
    // Piggy-back on the tap: also unlock audio for this session.
    speaker.current?.unlock();
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      toast(
        "Voice input isn't supported on this browser. You can type instead."
      );
      return;
    }
    // Reset chunks BEFORE we await getUserMedia so nothing races us.
    chunksRef.current = [];
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      const name = err && err.name;
      if (name === "NotAllowedError" || name === "SecurityError") {
        toast.error(
          "Mic access blocked. On iPhone: Settings → Stillroom → Microphone. On Chrome: tap the lock icon → Site settings → Microphone."
        );
      } else if (name === "NotFoundError") {
        toast.error("No microphone found on this device.");
      } else {
        toast.error("Couldn't start the mic. You can type instead.");
      }
      return;
    }

    // iOS Safari doesn't support webm — prefer it on Chrome/Firefox, fall
    // back to mp4 on iOS, and finally to the browser default.
    const preferred = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/mp4;codecs=mp4a.40.2",
      "audio/mp4",
    ];
    let mime = "";
    for (const m of preferred) {
      if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(m)) {
        mime = m;
        break;
      }
    }
    let rec;
    try {
      rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    } catch {
      rec = new MediaRecorder(stream);
    }
    const actualMime = rec.mimeType || mime || "audio/webm";
    rec.ondataavailable = (e) => {
      if (e.data && e.data.size) chunksRef.current.push(e.data);
    };
    rec.onstop = async () => {
      stream.getTracks().forEach((t) => t.stop());
      const blob = new Blob(chunksRef.current, { type: actualMime });
      // Diagnostic — surfaces in browser console for future iOS debugging.
      console.log("[mic] stop: chunks=%d bytes=%d mime=%s", chunksRef.current.length, blob.size, actualMime);
      if (blob.size < 250) {
        toast("Didn't catch that. Tap the mic and speak a little longer.");
        return;
      }
      const ext = (actualMime.split(";")[0].split("/")[1] || "webm").toLowerCase();
      try {
        const { text } = await transcribe(blob, `audio.${ext}`);
        if (text) await send(text);
        else toast("Didn't catch any words. Try again?");
      } catch (err) {
        toast.error("Couldn't transcribe. You can also just type.");
      }
    };
    recorderRef.current = rec;
    // iOS Safari's MediaRecorder requires a timeslice to reliably flush the
    // mp4 muxer — without it, .stop() can produce an empty final blob. 250ms
    // is a good tradeoff between chunk overhead and reliability.
    rec.start(250);
    setRecording(true);
  };

  const stopRecording = () => {
    if (!recording) return;
    setRecording(false);
    try {
      // Don't call requestData() before stop() — on iOS Safari that races
      // with the mp4 muxer and can drop the final buffer. Just stop cleanly;
      // the timeslice we set at start() ensures chunks are already in hand.
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stop();
      }
    } catch {
      /* ignore */
    }
  };

  const toggleMic = () => {
    if (recording) stopRecording();
    else startRecording();
  };

  const wrapNow = () => {
    abortRef.current?.();
    navigate(`/breathe/${sessionId}`);
  };

  return (
    <div
      className="flex flex-col overflow-hidden"
      style={{ height: `${vh}px` }}
      onClick={handleFirstGestureOpening}
    >
      {/* Header */}
      <div
        className="flex-none flex items-center justify-between px-6 py-4 bg-still-bg/70 backdrop-blur-xl border-b border-white/5"
        style={{ paddingTop: "max(1rem, env(safe-area-inset-top))" }}
        data-testid="chat-header"
      >
        <button
          data-testid="chat-close-btn"
          onClick={() => navigate("/")}
          className="relative z-10 -m-3 p-3 text-still-muted hover:text-still-fg transition-colors"
          aria-label="Close"
        >
          <X size={20} />
        </button>
        <div className="text-center">
          <div className="font-serif text-lg text-still-fg leading-none">Stillroom</div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-still-muted mt-1">
            Just here to listen.
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="mute-toggle"
            onClick={toggleMute}
            className="relative z-10 -m-2 p-2 text-still-muted hover:text-still-fg transition-colors"
            aria-label={muted ? "Unmute" : "Mute"}
          >
            {muted ? <VolumeX size={20} /> : <Volume2 size={20} />}
          </button>
          <button
            data-testid="wrap-btn"
            onClick={wrapNow}
            className="relative z-10 -m-2 p-2 text-still-muted hover:text-still-fg transition-colors"
            title="Wrap this up"
            aria-label="Wrap up"
          >
            <Sparkles size={18} />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        data-testid="messages-list"
        className="flex-1 overflow-y-auto px-6 pt-8 pb-6"
      >
        <div className="flex flex-col gap-6">
          {messages.map((m, i) => (
            <MessageBubble key={m.id || `${i}-${m.role}`} role={m.role} text={m.text} />
          ))}
          {streaming && streamText && (
            <MessageBubble role="assistant" text={streamText} streaming />
          )}
          {streaming && !streamText && (
            <div className="flex items-center gap-1.5 pl-1" data-testid="thinking-indicator">
              <span className="w-1.5 h-1.5 rounded-full bg-still-muted animate-pulse" />
              <span
                className="w-1.5 h-1.5 rounded-full bg-still-muted animate-pulse"
                style={{ animationDelay: "0.15s" }}
              />
              <span
                className="w-1.5 h-1.5 rounded-full bg-still-muted animate-pulse"
                style={{ animationDelay: "0.3s" }}
              />
            </div>
          )}
          {speaking && (
            <div className="flex items-end gap-1 h-4 pl-1" data-testid="speaking-indicator">
              {[0, 1, 2, 3].map((i) => (
                <span
                  key={i}
                  className="w-[3px] bg-still-breathe/70 rounded-full origin-bottom animate-wave"
                  style={{ height: "100%", animationDelay: `${i * 0.12}s` }}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Composer — normal flex child so the visual-viewport-driven page
          height naturally pushes it above the software keyboard on iOS. */}
      <div
        className="flex-none w-full px-4 pt-3 bg-gradient-to-t from-still-bg via-still-bg/95 to-transparent"
        style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
      >
        <div className="flex items-end gap-2 bg-still-surface border border-still-border rounded-3xl px-4 py-3">
          <textarea
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send(input);
              }
            }}
            placeholder={recording ? "Listening… tap mic to stop." : "Say what's true right now…"}
            rows={1}
            className="flex-1 bg-transparent resize-none outline-none text-still-fg placeholder:text-still-muted text-base py-1 max-h-32"
          />
          <button
            data-testid="mic-btn"
            onClick={toggleMic}
            className={`shrink-0 w-10 h-10 rounded-full grid place-items-center transition-colors ${
              recording ? "bg-still-vent text-still-bg animate-pulse" : "text-still-muted hover:text-still-fg"
            }`}
            aria-label={recording ? "Stop recording" : "Tap to speak"}
          >
            <Mic size={18} />
          </button>
          <button
            data-testid="send-btn"
            onClick={() => send(input)}
            disabled={streaming || !input.trim()}
            className="shrink-0 w-10 h-10 rounded-full bg-still-fg text-still-bg grid place-items-center disabled:opacity-30 transition-opacity"
            aria-label="Send"
          >
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ role, text, streaming }) {
  if (role === "assistant") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="font-serif text-xl sm:text-2xl leading-relaxed text-still-fg"
        data-testid="ai-message"
      >
        {text}
        {streaming && (
          <span className="inline-block w-2 h-5 ml-1 bg-still-breathe/60 align-middle animate-pulse" />
        )}
      </motion.div>
    );
  }
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="self-end bg-still-surface border border-still-border rounded-2xl rounded-br-sm px-4 py-2.5 text-still-fg/90 max-w-[85%] text-[15px] leading-relaxed"
      data-testid="user-message"
    >
      {text}
    </motion.div>
  );
}
